import { Injectable, Injector, OnDestroy, OnInit } from '@angular/core';

import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { cloneDeep } from 'lodash';
import { includes } from 'underscore';

import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { MentionsApi } from '../../../api/mentions/mentions.api';
import { MentionsSearchResponseModel } from '../../../api/mentions/model/mentions-search-response.model';
import { SearchCriteriaModel } from '../../../api/mentions/model/search-criteria.model';
import { SocialSearchService } from '../../Core/services/social-search.service';
import { TagGroupsService } from '../../../Common/Tags/services/tag-groups.service';
import { TagsService } from '../../../Common/Tags/services/tags.service';
import { ThemeService } from './theme.service';
import { JournodexRegionsService } from '../../../Common/Journodex/services/journodex-regions.service';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { ReportsSeriesMapperService } from '../../../Common/Reports/services/reports-series-mapper.service';
import { SourceGroupService } from '../../../Common/Sources/services/source-group.service';
import { ReportSeriesMentionRequestGeneratorOutput } from '../../../Common/Reports/models/report-series-mention-request-generator-output.model';
import { IndexeddbCoreCacheService } from '../../../Core/Cache/services/indexeddb-core-cache.service';
import { FilterTypeModel } from '../../../Common/Filter/models/abstract-filter-type.model';
import { FilterTypeCheckboxModel } from '../../../Common/Filter/models/filter-type-checkbox.model';
import { NetworkTypeEnum } from '../../Core/enums/network-type.enum';
import { socialFiltersEnum } from '../../Core/enums/social-filters.enum';
import { MediaTypeEnum } from '@Common/Basic/enums/media-type.enum';
import { mediaTypeToNetworkTypeMap } from '../models/network-type-to-media-type-model.map';
import { CurrencyConversionService } from '../../../Core/Users/services/currency-conversion.service';
import { SocialRoutingService } from '../../Core/services/social-routing.service';
import { MonitorConfigService } from '../../../Common/MonitorShared/services/monitor-config.service';
import { MediaTypeTreeFilterModel } from '../../../Common/Filter/models/media-type-tree-filter.model';
import { MentionSearchQueryModel } from '../../../Monitor/Shared/models/mention-search-query.model';
import { KeywordFilterModeEnum, MonitoringKeywordFilterModel } from '@Monitor/Shared/models/monitoring-keyword-filter.model';
import { SentimentModel } from '../../../Monitor/Shared/models/sentiment.model';
import { SavedFilterService } from '@Common/Filter/services/saved-filter.service';
import { DiscoverListsFilterService } from '../../../Common/Filter/services/discover-lists-filter.service';
import { ReportSeriesModel } from '@Common/Reports/models/report-series.model';
import { MediaSubscriptionApiService } from '@api/mediaSubscriptions/media-subscription.api.service';

/**
 * This class has attempted to learn from the issues found in the ReportsSeriesService, this service is responsible for:
 * 1. Requesting mention data in the fewest requests possible
 * 2. Caching that mention data
 *
 * It is not responsible for:
 * 1. Populating the mention search query
 * 2. Mapping the facet data to values usable by charts
 * 3. Listening to changes in some current resource
 *
 * The intention is for this service to accept arrays of series, when it gets an array of series, it will make as few requests as possible to fetch the data
 * required for them, it will then cache that data for a certain period of time.
 *
 * This service extends SocialSearchService because it shares the filters, it may make more sense to make a more clean version of SocialFilterService that both
 * this and the search service can extend, @TODO?
 */
@Injectable()
export class SocialSeriesService extends SocialSearchService implements OnDestroy {

    activeSearchFilters$: BehaviorSubject<FilterTypeModel[]> = new BehaviorSubject<FilterTypeModel[]>(null);
    public allNetworkTypes = [
        NetworkTypeEnum.all,
        NetworkTypeEnum.twitter,
        NetworkTypeEnum.facebook,
        NetworkTypeEnum.instagram,
        NetworkTypeEnum.youtube,
        NetworkTypeEnum.reddit,
    ];

    public availableNetworks$: Observable<NetworkTypeEnum[]> = combineLatest([
        this.flatFilters$.pipe(filter(x => !!x)),
        this.monitorConfigService.getAvailableSocialMediaTypes().pipe(filter(x => !!x))
    ]).pipe(
        map(
            ([flatFilters, availableMediaTypes]) => {
                const flatNetworkFilters = flatFilters.find(flatFilter => flatFilter.id === socialFiltersEnum.networkType);
                const provisionedNetworkTypes = availableMediaTypes.map(mediaType => mediaTypeToNetworkTypeMap.get(mediaType)).filter(network => !!network);

                let enabledNetworkTypes: NetworkTypeEnum[] = [NetworkTypeEnum.all, ...provisionedNetworkTypes];

                if (flatNetworkFilters?.getCurrentValue()?.mediaType?.length) {
                    const selectedMediaTypes: MediaTypeEnum[] = flatNetworkFilters.getCurrentValue().mediaType.map(x => x.id);
                    const selectedNetworkTypes = selectedMediaTypes
                        .map(mediaType => mediaTypeToNetworkTypeMap.get(mediaType))
                        .filter(network => provisionedNetworkTypes.includes(network));

                    enabledNetworkTypes = [NetworkTypeEnum.all, ...selectedNetworkTypes];
                }
                return enabledNetworkTypes;
            }
        )
    );

    constructor(
        protected tagsService: TagsService,
        protected tagGroupsService: TagGroupsService,
        protected journodexService: JournodexRegionsService,
        protected mentionsApi: MentionsApi,
        protected mediaSubscriptionApiService: MediaSubscriptionApiService,
        protected currentUserStateService: CurrentUserStateService,
        protected reportSeriesMapperService: ReportsSeriesMapperService,
        protected sourceGroupService: SourceGroupService,
        protected localizationService: LocalizationService,
        protected themeService: ThemeService,
        protected cacheService: IndexeddbCoreCacheService,
        protected currencyConversionService: CurrencyConversionService,
        protected socialRoutingService: SocialRoutingService,
        protected monitorConfigService: MonitorConfigService,
        protected savedFilterService: SavedFilterService,
        protected discoverListsFilterService: DiscoverListsFilterService,
        protected injector: Injector
    ) {
        super(
            tagsService,
            tagGroupsService,
            journodexService,
            mentionsApi,
            mediaSubscriptionApiService,
            currentUserStateService,
            reportSeriesMapperService,
            sourceGroupService,
            localizationService,
            themeService,
            currencyConversionService,
            socialRoutingService,
            monitorConfigService,
            savedFilterService,
            discoverListsFilterService,
            injector,
            cacheService
        );
    }

    /**
     * Applies the current filters onto the search
     */
    applyFilters(search: boolean = true) {
        super.applyFilters(false);
        this.activeSearchFilters$.next(this.allFilters);
        if (search) {
            this.doSearch(false);
        }
    }

    doSearch(fetchFacets: boolean = false): void {
        if (!this.agentIds?.length && !this.isArchiveSearch) {
            this.clearSearch();
            this.clearProviders();

            return;
        }

        if (!this.searchCriteria) {
            this.searchCriteria = this.createMentionSearchCriteriaModel();
        }

        if (!this.searchCriteria.query.sentiments?.length) {
            this.searchCriteria.query.sentiments = SentimentModel.getAll();
        }

        if (fetchFacets) {
            const search = cloneDeep(this.searchCriteria);
            const searchRequest: Observable<MentionsSearchResponseModel> = this.socialFacetSearch(search);
            // just to make sure it runs, in case the caller doesn't subscribe
            this.mainSearchRequest = searchRequest.subscribe({
                next: (result) => {
                    this.mainSearchRequest = null;
                    this.setTotalResultsToFilter(result.totalResults);
                },
                error: (err) => {
                    this.mainSearchRequest = null;
                }
            });
        }

        if (this.searchCriteriaMap?.size) {
            this.fetchDataForSeries(true);
        }
    }

    handleObservers(responseObservable: any, searchCriteriaMapItem: any, refreshAll: any): void {
        // do the check here, because if we allow them to subscribe, we will make unecessary API requests
        if (!this.agentIds?.length && !this.isArchiveSearch) {
            for (const singleSeries of searchCriteriaMapItem.series) {
                if (singleSeries.mentions.dataMapper) {
                    singleSeries.dataProvider.next([]);
                }
                if (singleSeries.mentions.mentionDataMapper) {
                    singleSeries.mentionProvider.next([]);
                }
            }
        } else {
            responseObservable.subscribe({
                next: (response) => {
                    searchCriteriaMapItem.response = response;

                    for (const singleSeries of searchCriteriaMapItem.series) {
                        singleSeries.fullSearch.query = new MentionSearchQueryModel(response.combinedQuery);
                        singleSeries.fullSearch.filterQuery = null;
                        if (
                            singleSeries.mentions.dataMapper &&
                            (singleSeries.dataProvider.value === null || refreshAll)
                        ) {

                            if (singleSeries.hasPreviousPeriod && singleSeries.mentions.isPreviousPeriod) {
                                singleSeries.previousPeriodDataProvider.next(singleSeries.mentions.dataMapper(response, searchCriteriaMapItem.request));
                            } else {
                                singleSeries.dataProvider.next(singleSeries.mentions.dataMapper(response, searchCriteriaMapItem.request,singleSeries));
                            }
                        }

                        if (
                            singleSeries.mentions.mentionDataMapper &&
                            (singleSeries.mentionProvider.value === null || refreshAll)
                        ) {
                            singleSeries.mentionProvider.next(singleSeries.mentions.mentionDataMapper(response));
                        }
                    }
                },
                error: (err) => {
                    if ((err?.status === 400) && (err.error?.details?.[0])) {
                        this.handleSavedFilterConflictError(err.error.details[0]);
                    } else {
                        throw err;
                    }
                }
            });
        }
    }

    /**
     * is a function that sets ALL dataProvider & mentionProvider to empty array to display there is not data available
     * should only be called where there is no social topics selected
     */
    clearProviders(): void {
        const searchCriteriaMapItems: SearchCriteriaMapItem[] = Array.from(this.searchCriteriaMap.values());
        for (const searchCriteriaMapItem of searchCriteriaMapItems) {
            for (const singleSeries of searchCriteriaMapItem.series) {
                if (singleSeries.mentions.dataMapper) {
                    singleSeries.dataProvider.next([]);
                }
                if (singleSeries.mentions.mentionDataMapper) {
                    singleSeries.mentionProvider.next([]);
                }
            }
        }
    }

    applyActiveFilters(filters: FilterTypeModel[]) {
        filters.forEach(activeFilter => {
            const matchFilter = this.allFilters.find(singleFilter => singleFilter.id === activeFilter.id);
            if (matchFilter) {
                if (matchFilter instanceof FilterTypeCheckboxModel || matchFilter instanceof MediaTypeTreeFilterModel) {
                    (matchFilter as FilterTypeCheckboxModel).options = (activeFilter as FilterTypeCheckboxModel).options;
                }

                if (matchFilter instanceof MonitoringKeywordFilterModel && activeFilter instanceof MonitoringKeywordFilterModel &&
                    activeFilter.getFilterMode() === KeywordFilterModeEnum.expert) {
                    matchFilter.setFilterMode(KeywordFilterModeEnum.expert);
                }
                matchFilter.setValue(activeFilter.getValue());
                matchFilter.setIncludeExclude(activeFilter.includeExclude);
            }
        });
        this.setFilters(this.allFilters);
        this.applyFilters(true);
    }
}

interface SearchCriteriaMapItem {
    request: SearchCriteriaModel;
    response: MentionsSearchResponseModel;
    mentionRequest?: ReportSeriesMentionRequestGeneratorOutput;
    series: ReportSeriesModel[];
}
